/**
 * Phase 3 - Help & Documentation page.
 *
 * Operator-facing quickstart for each FOCTTA module + keyboard shortcuts
 * + FAQ. Linked from the topbar HelpMenu and accessible at /help.
 *
 * Content is sourced inline (no backend dependency) so the page works
 * offline + in air-gapped deployments. Sections + search render
 * client-side.
 */
import { useMemo, useState } from 'react';
import {
  HelpCircle, Search, ChevronRight, BookOpen, Keyboard, MessageCircle,
  Database, ShieldAlert, Activity as ActivityIcon, Scale, Settings,
} from 'lucide-react';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { PageContainer, PageHeader } from '@/components/viz/PageHeader';

interface HelpEntry {
  id: string;
  category: 'getting-started' | 'discovery' | 'detection' | 'activity' | 'compliance' | 'admin' | 'shortcuts' | 'faq';
  title: string;
  body: string;
}

const ENTRIES: HelpEntry[] = [
  // Getting started
  {
    id: 'gs-1', category: 'getting-started', title: 'First-time setup',
    body: 'After your administrator invites you, click the link in your invitation email, set a password, then sign in at app.foctta.com or your tenant subdomain. Your role (DPO, Compliance Analyst, Legal Reviewer, IT Admin, SecOps, Read-Only) determines which modules appear in the sidebar.',
  },
  {
    id: 'gs-2', category: 'getting-started', title: 'Tenant URL structure',
    body: 'Your tenant lives at <slug>.foctta.com. Discovery is at /discovery, Activity (DAM) at /activity, Detection (DDR) at /detection, Compliance at /compliance, Admin at /admin. The same URL works whether you arrived via the FOCTTA bundle, standalone Discovery, or standalone Core mode.',
  },
  {
    id: 'gs-3', category: 'getting-started', title: 'The Synthetic-Error Contract',
    body: 'When DDR blocks a query, the SQL error returned to the caller carries a Request ID (e.g. BLK-A8C…). That same ID appears in the audit chain, the SOC SIEM ticket, the Notification Center entry, and the Why-Blocked screen. Paste it into any of those views to trace one decision end-to-end.',
  },
  // Discovery (DSPM)
  {
    id: 'd-1', category: 'discovery', title: 'Connecting your first data source',
    body: 'Open Admin → Integrations → Marketplace, search the connector you need (Snowflake, BigQuery, Oracle, MSSQL, SMB, S3), click Install, paste credentials. Discovery starts a scan within 60s. Watch progress in Discovery → Scans.',
  },
  {
    id: 'd-2', category: 'discovery', title: 'Reading a finding',
    body: 'Discovery → Findings lists every PII column it found. Severity is auto-assigned by classifier tier (Tier-1 regex, Tier-2 NER, Tier-3 graph-context, Tier-4 pgvector). Click a row to see the Request ID, recommended playbook, and the linked DDR decisions (Phase 3 F-2.8 drill-down).',
  },
  {
    id: 'd-3', category: 'discovery', title: 'No-Direct-DB-Access mode',
    body: 'For customers who cannot grant Discovery a database role, the No-DB-Access wizard lets you upload a schema dump (CSV/SQL). Discovery classifies columns from the schema only. Open Discovery → No-DB-Access to start.',
  },
  // Activity (DAM)
  {
    id: 'a-1', category: 'activity', title: 'Live query stream',
    body: 'Activity → Stream shows every query touching your data, in real time. Filter by actor, database, severity, decision. The stream uses Server-Sent Events; pause/resume via the toolbar.',
  },
  {
    id: 'a-2', category: 'activity', title: 'Forensic Timeline (Phase 3 F-4.6)',
    body: 'Activity → Timeline shows activity events, DDR decisions, Discovery findings, and Compliance breaches interleaved by time. Drag the brush to zoom. Click "Export forensic JSON" to download a sha256-manifest bundle for chain-of-custody.',
  },
  {
    id: 'a-3', category: 'activity', title: 'UEBA, Sonar, P-Wave engines',
    body: 'Activity ships three behavioural engines. UEBA flags actor-level deviations (z-score > 3). Submarine Sonar surfaces slow-walk reconnaissance patterns. P-Wave fires earthquake-style early warnings (cold-table burst, off-hour access).',
  },
  // Detection (DDR)
  {
    id: 'dd-1', category: 'detection', title: 'How DDR decides',
    body: 'When a query lands on your protected gateway, DDR runs three independent engines (Rule, UEBA, Policy Graph) and applies aerospace triple-redundancy. Block wins over alert wins over allow. Every decision lands in Detection → Decisions with a Request ID.',
  },
  {
    id: 'dd-2', category: 'detection', title: 'Why-Blocked drawer (Phase 3 F-4.3)',
    body: 'Click any blocked decision → the drawer shows the matched rule, all three engine votes, and per-pillar reason rows: consent state, retention horizon, vendor DPA on file, cross-border verdict, DPDPA §10 SDF flag, UEBA z-score, BU owner, manager chain. Copy the Request ID with one click.',
  },
  {
    id: 'dd-3', category: 'detection', title: 'Break-Glass elevation',
    body: 'For genuine emergencies (incident response, audit override), Detection → Break-Glass lets a SOC operator request temporary elevation. A second approver (DPO or Legal Reviewer) must approve. Every break-glass session writes a hash-chained audit entry.',
  },
  // Compliance
  {
    id: 'c-1', category: 'compliance', title: 'DPIA workflow (Phase 3 F-5.6)',
    body: 'Compliance → DPIA: click "New DPIA" → "Pre-fill from inventory" auto-populates data categories and risk rows from your Discovery findings. After review + 2-person approval, click "Export" to download a regulator-grade HTML document (print to PDF for DPB filing).',
  },
  {
    id: 'c-2', category: 'compliance', title: 'Breach incident dispatch',
    body: 'When DDR fires a high/critical block, a breach incident draft is auto-created and a Notification Center alert is broadcast to DPO/Compliance Analyst/Legal Reviewer roles. Click "Notify DPB" - FOCTTA sends a templated email to the configured regulator inbox, with channel evidence + sha256 message id captured in the audit chain.',
  },
  {
    id: 'c-3', category: 'compliance', title: 'Section 9 SDF Board report',
    body: 'For Significant Data Fiduciaries under DPDPA s.10: Compliance → Section 9 lists your DPO appointment, DPIA review status, periodic audit status. Click "Export Board Report" to download the attestation HTML (print to PDF for Board sign-off).',
  },
  // Admin
  {
    id: 'ad-1', category: 'admin', title: 'Inviting users',
    body: 'Admin → Auth → IdP Connections: connect Okta/Azure AD/Google via SAML or OIDC. Users provisioned via SCIM appear in Admin → Custom Roles. Role assignments take effect at next sign-in.',
  },
  {
    id: 'ad-2', category: 'admin', title: 'Business Units + BU scoping',
    body: 'Admin → Business Units lets you carve your tenant into BUs (e.g. Wealth Management India, Mutual Funds, Insurance). Users get BU-scoped grants. Findings, decisions, and breaches are visible only to BUs the user is allowed to access.',
  },
  {
    id: 'ad-3', category: 'admin', title: 'Edge pod fleet',
    body: 'For customer-VPC scanning (no data leaves your network): Admin → Edge Fleet shows every pod, its heartbeat, last scan, and lag. Click a pod to see live logs + decommission. New pods provision through Admin → Edge Provisioning with a bootstrap token.',
  },
  // Keyboard shortcuts
  {
    id: 'k-1', category: 'shortcuts', title: 'Cmd+K - Federated search',
    body: 'Press Cmd+K (Mac) or Ctrl+K (Windows/Linux) anywhere to open the Federated Search palette. Searches across findings, activity events, decisions, audit entries, integrations, users, pages, and help articles. Use arrow keys to navigate, Enter to select.',
  },
  {
    id: 'k-2', category: 'shortcuts', title: 'Cmd+/ - Open Help',
    body: 'Press Cmd+/ (Mac) or Ctrl+/ (Windows/Linux) anywhere to open this Help page.',
  },
  {
    id: 'k-3', category: 'shortcuts', title: 'Esc - Close dialogs',
    body: 'Escape closes any open dialog, drawer, or modal. Cmd+K palette too.',
  },
  // FAQ
  {
    id: 'f-1', category: 'faq', title: 'Why do I see "tenant-context: missing"?',
    body: 'Your session expired or the cookie was cleared. Click Sign In again from the topbar UserMenu, or reload the page.',
  },
  {
    id: 'f-2', category: 'faq', title: 'Why can\'t I see a module in the sidebar?',
    body: 'Sidebar nav is entitlement-driven. If your tenant doesn\'t have a Detection / Compliance / Activity license, that section is hidden. Ask your IT Admin to check Admin → Licenses.',
  },
  {
    id: 'f-3', category: 'faq', title: 'How are notifications hash-chained?',
    body: 'Each notification\'s content_hash is computed from prev_id + tenant_id + user_id + type + severity + title + body + timestamp. Editing any past notification breaks the chain - that\'s detectable by the verify-chain admin endpoint. This is the same audit-trail guarantee as the main FOCTTA audit chain.',
  },
  {
    id: 'f-4', category: 'faq', title: 'Can I run FOCTTA Discovery without FOCTTA Core?',
    body: 'Yes - Discovery is fully standalone. Every Core integration is optional (consent enrichment, audit egress, BU sync). When Core is paired, the bridge unlocks cross-product features; when absent, Discovery degrades to local-only sources gracefully.',
  },
];

const CATEGORY_LABELS: Record<HelpEntry['category'], { label: string; icon: React.ComponentType<{ className?: string }> }> = {
  'getting-started': { label: 'Getting started', icon: BookOpen },
  discovery:         { label: 'Discovery (DSPM)', icon: Database },
  detection:         { label: 'Detection (DDR)', icon: ShieldAlert },
  activity:          { label: 'Activity (DAM)', icon: ActivityIcon },
  compliance:        { label: 'Compliance', icon: Scale },
  admin:             { label: 'Admin', icon: Settings },
  shortcuts:         { label: 'Keyboard shortcuts', icon: Keyboard },
  faq:               { label: 'FAQ', icon: MessageCircle },
};

export function HelpPage() {
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return ENTRIES;
    return ENTRIES.filter(
      (e) => e.title.toLowerCase().includes(q) || e.body.toLowerCase().includes(q),
    );
  }, [query]);

  const grouped = useMemo(() => {
    const out: Record<string, HelpEntry[]> = {};
    for (const e of filtered) {
      (out[e.category] ??= []).push(e);
    }
    return out;
  }, [filtered]);

  const orderedCats: HelpEntry['category'][] = [
    'getting-started', 'discovery', 'detection', 'activity', 'compliance',
    'admin', 'shortcuts', 'faq',
  ];

  return (
    <PageContainer>
      <PageHeader title="Help & Documentation" />

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search help articles..."
          className="w-full rounded-md border border-border bg-background pl-10 pr-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
        />
      </div>

      {filtered.length === 0 ? (
        <GlassCard className="p-8 text-center">
          <HelpCircle className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
          <div className="text-sm text-muted-foreground">No matching articles.</div>
          <div className="text-xs text-muted-foreground mt-1">
            Try a different keyword, or contact support@foctta.com.
          </div>
        </GlassCard>
      ) : (
        orderedCats.map((cat) => {
          const entries = grouped[cat];
          if (!entries || entries.length === 0) return null;
          const { label, icon: Icon } = CATEGORY_LABELS[cat];
          return (
            <GlassCard key={cat} className="mb-4">
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {label}
                  <Badge variant="glass" className="text-[9px] ml-auto">{entries.length}</Badge>
                </CardTitle>
                <CardDescription className="text-xs">
                  {cat === 'shortcuts' ? 'Keyboard combos available everywhere.' : ''}
                </CardDescription>
              </CardHeader>
              <div className="divide-y divide-border/20">
                {entries.map((e) => {
                  const isOpen = openId === e.id;
                  return (
                    <div key={e.id} className="px-4 py-3">
                      <button
                        type="button"
                        onClick={() => setOpenId(isOpen ? null : e.id)}
                        className="w-full text-left flex items-center gap-2"
                      >
                        <ChevronRight
                          className={`h-3.5 w-3.5 text-muted-foreground transition-transform ${isOpen ? 'rotate-90' : ''}`}
                        />
                        <span className="text-sm font-medium flex-1">{e.title}</span>
                      </button>
                      {isOpen && (
                        <p className="mt-2 ml-5 text-sm text-foreground/85 leading-relaxed whitespace-pre-wrap">
                          {e.body}
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            </GlassCard>
          );
        })
      )}

      <div className="mt-6 text-xs text-muted-foreground text-center">
        Need more help? Email <a href="mailto:support@foctta.com" className="underline">support@foctta.com</a> - include your tenant slug and any Request IDs.
      </div>
    </PageContainer>
  );
}
